/**
 * One company transition at a time (TF-COMPANY-SWITCH, contract
 * docs/design/2026-09-14-company-switch-contract.md). The steps: no-op on the
 * current company; a dirty editor asks first; writes in flight are finished
 * first; then the switch request; then the verified answer (token, role,
 * active company all present and the company the one asked for) commits a
 * new generation, the cache is dropped and the page tree remounts under the
 * new company. Failures keep the former company coherent, or name the lost
 * membership.
 */
import { ApiError, get, post } from '@/api/client';
import { cookieToken, getToken, useAuth } from '@/auth/store';
import { T } from '@/i18n';
import {
  announce,
  commitGeneration,
  ctx,
  hasDirty,
  sessionChangedElsewhere,
  setDestinationCheck,
  setPhase,
  whenWritesSettle,
} from '@/shell/context';
import { type Tenant, useTenant } from '@/shell/tenant';
import { useLocation } from 'react-router-dom';

export function useCompanySwitch() {
  const user = useAuth((s) => s.user);
  const setSession = useAuth((s) => s.setSession);
  const { tenant, tenants } = useTenant();
  const loc = useLocation();

  async function proceed(target: Tenant) {
    const from = tenant?.name ?? ctx().companyName ?? '';
    // the context this transition belongs to, captured before the first wait
    // (TF-SWITCH-RECOVERY): a logout, another tab's session change or a newer
    // context during any wait cancels this continuation entirely
    const origin = { gen: ctx().gen, token: getToken(), userId: user?.id ?? null };
    const mine = () =>
      origin.token != null &&
      ctx().gen === origin.gen &&
      getToken() === origin.token &&
      !sessionChangedElsewhere(getToken(), cookieToken());
    if (!mine()) return;
    if (ctx().pendingWrites > 0) {
      setPhase({ kind: 'finishing', target: { id: target.id, name: target.name } });
      await whenWritesSettle();
      if (!mine()) return; // the session changed while the write was finishing
    }
    if (ctx().unknownWrites > 0) {
      // a write ended without an outcome, now or earlier in this context: the
      // person checks it before the company changes; nothing is retried here
      setPhase({ kind: 'unknown-write' });
      return;
    }
    setPhase({ kind: 'switching', target: { id: target.id, name: target.name } });
    const failed = () => {
      setPhase({ kind: 'failed' });
      announce(T('Kunne ikke bytte bedrift. Du er fortsatt i {company}.', { company: from }));
    };
    /** the switch failed and the active company could not be established: neither company may be claimed */
    const unverified = () => {
      setPhase({ kind: 'unverified' });
      announce(T('Kunne ikke bekrefte hvilken bedrift som er aktiv. Prøv igjen.'));
    };
    let r: { token?: unknown; role?: unknown; active_tenant?: unknown } | null = null;
    try {
      r = await post('/tenants/switch-tenant', { tenant_id: target.id });
    } catch (e) {
      if (!mine()) return; // the session ended or moved on meanwhile
      if (e instanceof ApiError && e.status === 401) return; // the request layer has logged out
      // lost or refused: only a verified answer may name the active company
      try {
        const still = await get<Tenant>('/tenant');
        if (!mine()) return;
        if (tenant && still.id === tenant.id) return failed(); // verified: the former company is still active
        return unverified(); // answered, but not the company this screen shows
      } catch (e2) {
        if (!mine()) return;
        if (e2 instanceof ApiError && (e2.status === 403 || e2.status === 401)) {
          setPhase({ kind: 'access-lost' });
          return;
        }
        return unverified(); // the check itself failed: nothing is verified
      }
    }
    if (!mine()) return;
    const token = r && typeof r.token === 'string' ? r.token : null;
    const role = r && typeof r.role === 'string' ? r.role : null;
    const active = r ? Number(r.active_tenant) : Number.NaN;
    // an incomplete answer never borrows the former role, and a replaced session never commits
    if (!token || !role || active !== target.id || !user || user.id !== origin.userId) return failed();
    setSession(token, { ...user, role });
    commitGeneration(user.id, target.id, target.name);
    // the transition stays open, and the menu disabled, until the new company's
    // settings answer under the new generation: the shell settles it
    setDestinationCheck({ id: target.id, name: target.name, role, fromPath: loc.pathname });
  }

  return {
    switchTo(id: number) {
      const target = tenants.find((t) => t.id === id);
      if (!target || (tenant && tenant.id === id)) return; // the current company: nothing to do
      const k = ctx().phase.kind;
      if (k !== 'idle' && k !== 'failed') return; // one transition at a time
      if (hasDirty()) {
        setPhase({ kind: 'confirm', target: { id: target.id, name: target.name } });
        return;
      }
      void proceed(target);
    },
    /** the dirty dialog: stay in the current company */
    stay: () => setPhase({ kind: 'idle' }),
    /** the dirty dialog: drop the drafts and switch */
    discardAndSwitch: () => {
      const p = ctx().phase;
      if (p.kind !== 'confirm') return;
      const target = tenants.find((t) => t.id === p.target.id);
      if (target) void proceed(target);
      else setPhase({ kind: 'idle' });
    },
    dismiss: () => setPhase({ kind: 'idle' }),
  };
}
